import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api.js";
import { createBill, updateBill } from "../../lib/api-modules/bills.js";
import {
  BILL_RECURRENCE_LABELS,
  PAYMENT_METHOD_LABELS,
  type Bill,
  type BillRecurrence,
  type CreateBillInput,
  type PaymentMethod,
} from "../../lib/api-modules/types.js";
import { centsToDollars, dollarsToCents } from "../../lib/util/money.js";
import { ErrorMessage } from "../auth/error-message.js";
import {
  Button,
  CheckboxField,
  DateField,
  NumberField,
  SelectField,
  TextField,
  TextareaField,
} from "../design-system/index.js";

/**
 * <BillForm> — create/edit a household bill (Phase 5 Session 2;
 * design-system restyle in Phase 5 Session 6).
 *
 * Required: name, amount (dollars), due_date, recurrence, start_date.
 * Optional: payee, payment_method, account_id (ULID free-text in v1),
 * category (free-text in v1), autopay flag, notes, description.
 *
 * Phase 5.6 additive: expected_uses_per_month (cost-per-use override),
 * price_change_alert_threshold_cents / _pct (per-bill subscription-
 * audit threshold override). Both surfaced under a collapsed
 * "Subscription tuning" disclosure; defaults work fine for most users.
 */

const RECURRENCE_OPTIONS: ReadonlyArray<{
  value: BillRecurrence;
  label: string;
}> = (Object.keys(BILL_RECURRENCE_LABELS) as BillRecurrence[]).map((v) => ({
  value: v,
  label: BILL_RECURRENCE_LABELS[v],
}));

const PAYMENT_METHOD_OPTIONS: ReadonlyArray<{
  value: PaymentMethod | "";
  label: string;
}> = [
  { value: "", label: "—" },
  ...(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[]).map((v) => ({
    value: v,
    label: PAYMENT_METHOD_LABELS[v],
  })),
];

interface BillFormProps {
  readonly mode: "create" | "edit";
  readonly bill?: Bill;
  readonly onClose: () => void;
}

export function BillForm({ mode, bill, onClose }: BillFormProps) {
  const queryClient = useQueryClient();
  const isEdit = mode === "edit";

  const today = new Date().toISOString().slice(0, 10);

  const [name, setName] = useState<string>(bill?.name ?? "");
  const [amountDollars, setAmountDollars] = useState<string>(
    bill ? centsToDollars(bill.amount_cents) : "",
  );
  const [dueDate, setDueDate] = useState<string>(bill?.due_date ?? today);
  const [startDate, setStartDate] = useState<string>(
    bill?.start_date ?? today,
  );
  const [recurrence, setRecurrence] = useState<BillRecurrence>(
    bill?.recurrence ?? "monthly",
  );
  const [autopay, setAutopay] = useState<boolean>(bill?.autopay ?? false);
  const [payee, setPayee] = useState<string>(bill?.payee ?? "");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | "">(
    bill?.payment_method ?? "",
  );
  const [category, setCategory] = useState<string>(bill?.category ?? "");
  const [notes, setNotes] = useState<string>(bill?.notes ?? "");
  const [description, setDescription] = useState<string>(
    bill?.description ?? "",
  );
  const [usesPerMonth, setUsesPerMonth] = useState<string>(
    bill?.expected_uses_per_month?.toString() ?? "",
  );
  const [thresholdDollars, setThresholdDollars] = useState<string>(
    bill?.price_change_alert_threshold_cents !== undefined
      ? centsToDollars(bill.price_change_alert_threshold_cents)
      : "",
  );
  const [thresholdPct, setThresholdPct] = useState<string>(
    bill?.price_change_alert_threshold_pct?.toString() ?? "",
  );
  const [showTuning, setShowTuning] = useState<boolean>(
    Boolean(
      bill?.expected_uses_per_month ??
        bill?.price_change_alert_threshold_cents ??
        bill?.price_change_alert_threshold_pct,
    ),
  );
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const trimmedName = name.trim();
      const cents = dollarsToCents(amountDollars);
      const trimmedPayee = payee.trim();
      const trimmedCategory = category.trim();
      const trimmedNotes = notes.trim();
      const trimmedDescription = description.trim();
      const usesNum = usesPerMonth.trim().length > 0
        ? Number.parseInt(usesPerMonth, 10)
        : undefined;
      const thresholdCentsNum =
        thresholdDollars.trim().length > 0
          ? dollarsToCents(thresholdDollars)
          : undefined;
      const thresholdPctNum =
        thresholdPct.trim().length > 0
          ? Number.parseInt(thresholdPct, 10)
          : undefined;

      if (isEdit && bill) {
        const input: Record<string, unknown> = {
          name: trimmedName,
          amount_cents: cents,
          due_date: dueDate,
          recurrence,
          start_date: startDate,
          autopay,
        };
        if (trimmedPayee.length > 0) input["payee"] = trimmedPayee;
        if (trimmedCategory.length > 0) input["category"] = trimmedCategory;
        if (paymentMethod.length > 0)
          input["payment_method"] = paymentMethod;
        if (trimmedNotes.length > 0) input["notes"] = trimmedNotes;
        if (trimmedDescription.length > 0)
          input["description"] = trimmedDescription;
        if (usesNum !== undefined && Number.isFinite(usesNum) && usesNum > 0)
          input["expected_uses_per_month"] = usesNum;
        if (
          thresholdCentsNum !== undefined &&
          Number.isFinite(thresholdCentsNum) &&
          thresholdCentsNum >= 0
        )
          input["price_change_alert_threshold_cents"] = thresholdCentsNum;
        if (
          thresholdPctNum !== undefined &&
          Number.isFinite(thresholdPctNum) &&
          thresholdPctNum >= 0
        )
          input["price_change_alert_threshold_pct"] = thresholdPctNum;
        return updateBill(bill.bill_id, input);
      }
      const input: CreateBillInput = {
        name: trimmedName,
        amount_cents: cents,
        due_date: dueDate,
        recurrence,
        start_date: startDate,
        autopay,
      };
      if (trimmedPayee.length > 0) input.payee = trimmedPayee;
      if (trimmedCategory.length > 0) input.category = trimmedCategory;
      if (paymentMethod !== "") input.payment_method = paymentMethod;
      if (trimmedNotes.length > 0) input.notes = trimmedNotes;
      if (trimmedDescription.length > 0) input.description = trimmedDescription;
      if (usesNum !== undefined && Number.isFinite(usesNum) && usesNum > 0)
        input.expected_uses_per_month = usesNum;
      if (
        thresholdCentsNum !== undefined &&
        Number.isFinite(thresholdCentsNum) &&
        thresholdCentsNum >= 0
      )
        input.price_change_alert_threshold_cents = thresholdCentsNum;
      if (
        thresholdPctNum !== undefined &&
        Number.isFinite(thresholdPctNum) &&
        thresholdPctNum >= 0
      )
        input.price_change_alert_threshold_pct = thresholdPctNum;
      return createBill(input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["bills"] });
      onClose();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't save bill. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    if (name.trim().length === 0) {
      setInlineError("Name is required.");
      return;
    }
    const cents = dollarsToCents(amountDollars);
    if (cents <= 0) {
      setInlineError("Amount must be greater than 0.");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
      setInlineError("Due date is required.");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
      setInlineError("Start date is required.");
      return;
    }
    mutation.mutate();
  }

  const isSubscription = recurrence !== "one_time";

  return (
    <form
      onSubmit={onSubmit}
      aria-label={isEdit ? "Edit bill" : "Add bill"}
      className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-elevated)] p-4"
    >
      <header className="mb-4 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-[var(--color-text)]">
          {isEdit ? "Edit bill" : "Add bill"}
        </h4>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <TextField
          containerClassName="sm:col-span-2"
          label="Name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={mutation.isPending}
          placeholder="ConEd electric"
          autoFocus
        />
        <NumberField
          label="Amount (USD)"
          required
          step="0.01"
          min="0"
          value={amountDollars}
          onChange={(e) => setAmountDollars(e.target.value)}
          disabled={mutation.isPending}
          placeholder="125.00"
        />
        <SelectField
          label="Recurrence"
          required
          value={recurrence}
          onChange={(e) => setRecurrence(e.target.value as BillRecurrence)}
          disabled={mutation.isPending}
        >
          {RECURRENCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </SelectField>
        <DateField
          label="Next due"
          required
          value={dueDate}
          onChange={(e) => {
            setDueDate(e.target.value);
            if (!isEdit) setStartDate(e.target.value);
          }}
          disabled={mutation.isPending}
        />
        <DateField
          label="Anchor day (start date)"
          helper="Day-of-month from this date is the recurrence anchor. Leave equal to next due for new bills."
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          disabled={mutation.isPending}
        />
        <TextField
          label="Payee"
          helper="Optional"
          value={payee}
          onChange={(e) => setPayee(e.target.value)}
          disabled={mutation.isPending}
          placeholder="Consolidated Edison"
        />
        <SelectField
          label="Payment method"
          helper="Optional"
          value={paymentMethod}
          onChange={(e) =>
            setPaymentMethod(e.target.value as PaymentMethod | "")
          }
          disabled={mutation.isPending}
        >
          {PAYMENT_METHOD_OPTIONS.map((o) => (
            <option key={o.value || "none"} value={o.value}>
              {o.label}
            </option>
          ))}
        </SelectField>
        <TextField
          label="Category"
          helper="Optional. Affects cost-per-use defaults (streaming, gym, software…)."
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          disabled={mutation.isPending}
          placeholder="Utilities"
        />
        <CheckboxField
          className="sm:col-span-2"
          label="Autopay (informational only — Hearth doesn't make payments)"
          checked={autopay}
          onChange={(e) => setAutopay(e.target.checked)}
          disabled={mutation.isPending}
        />
        <TextField
          containerClassName="sm:col-span-2"
          label="Description"
          helper="Optional"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={mutation.isPending}
          maxLength={500}
        />
        <TextareaField
          containerClassName="sm:col-span-2"
          label="Notes"
          helper="Optional"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={mutation.isPending}
          maxLength={2000}
        />
      </div>

      {isSubscription && (
        <div className="mt-4 border-t border-[var(--color-border-subtle)] pt-3">
          <button
            type="button"
            onClick={() => setShowTuning((s) => !s)}
            className="text-xs text-[var(--color-muted)] hover:text-[var(--color-text)] hover:underline"
          >
            {showTuning ? "Hide" : "Show"} subscription tuning (cost-per-use,
            price-change threshold)
          </button>
          {showTuning && (
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <NumberField
                label="Expected uses / month"
                helper="Override the cost-per-use estimator. Leave blank to use category default."
                step="1"
                min="1"
                value={usesPerMonth}
                onChange={(e) => setUsesPerMonth(e.target.value)}
                disabled={mutation.isPending}
                placeholder="auto"
              />
              <NumberField
                label="Alert threshold (USD)"
                helper="Default $5. Set 0 to alert on any change."
                step="0.01"
                min="0"
                value={thresholdDollars}
                onChange={(e) => setThresholdDollars(e.target.value)}
                disabled={mutation.isPending}
                placeholder="5.00"
              />
              <NumberField
                label="Alert threshold (%)"
                helper="Default 10. Set 0 to alert on any change."
                step="1"
                min="0"
                max="100"
                value={thresholdPct}
                onChange={(e) => setThresholdPct(e.target.value)}
                disabled={mutation.isPending}
                placeholder="10"
              />
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={mutation.isPending}
        >
          {mutation.isPending
            ? "Saving…"
            : isEdit
              ? "Save bill"
              : "Add bill"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
      </div>

      {inlineError && (
        <div className="mt-3">
          <ErrorMessage>{inlineError}</ErrorMessage>
        </div>
      )}
    </form>
  );
}
